// ============================================================
// Configuration — endpoints come from WordPress, not from here
// ============================================================
// The API environment is NOT set in code. It comes from WordPress:
// Settings → Univista Environment (includes/environment-settings.php),
// injected as `window.univistaEnv` by wp_localize_script(). Each site
// (local, dev, production) keeps its own value, so deploying the theme
// never changes which API the form talks to.
//
// If the setting never arrives (cached HTML, a deploy that missed
// functions.php), decide by hostname: the real domain uses the real APIs,
// anything else uses the test ones. A single default cannot be safe for
// both sides.
const PRODUCTION_HOSTS = ['univistafranchise.com', 'www.univistafranchise.com'];

const ENV_PRESETS = {
  test: {
    environment: 'test',
    apiBaseUrl: 'https://testapi.univistainsurance.com/api',
    citiesApiBase: 'https://test220appapi.univistainsurance.com/api',
  },
  production: {
    environment: 'production',
    apiBaseUrl: 'https://api.univistainsurance.com/api',
    citiesApiBase: 'https://220appapi.univistainsurance.com/api',
  },
};

const FALLBACK_ENV = PRODUCTION_HOSTS.includes(window.location.hostname)
  ? ENV_PRESETS.production
  : ENV_PRESETS.test;

const ENV = window.univistaEnv || FALLBACK_ENV;

const CONFIG = {
  ENVIRONMENT: ENV.environment || FALLBACK_ENV.environment,

  // API Endpoints
  CITIES_API_BASE: `${ENV.citiesApiBase || FALLBACK_ENV.citiesApiBase}/dataservice`,
  FRANCHISE_API_URL: `${ENV.apiBaseUrl || FALLBACK_ENV.apiBaseUrl}/franchise/request`,

  // reCAPTCHA Enterprise (same key in every environment)
  RECAPTCHA_SITE_KEY: '6LfB1korAAAAAJkuV0-6HejEf6NPHQydqYQWqWsf',
  RECAPTCHA_SCRIPT_URL: 'https://www.google.com/recaptcha/enterprise.js',

  // Form defaults
  FORM_SOURCE: 'Website',

  // Language of the page the lead was submitted from, as the API expects it.
  // Same rule as get_site_language() in functions.php: the /es/ URL prefix.
  LANGUAGE_IDS: { en: 1, es: 2 },
};

const pageLanguage = () => (window.location.pathname.startsWith('/es/') ? 'es' : 'en');

if (CONFIG.ENVIRONMENT !== 'production') {
  console.info('[univista] API environment:', CONFIG.ENVIRONMENT, CONFIG.FRANCHISE_API_URL);
}

document.addEventListener("DOMContentLoaded", () => {
  // Dynamic City Fetch logic with Custom Stylable Autocomplete for CF7 form
  const stateSelects = document.querySelectorAll('select[name="state"]');
  stateSelects.forEach(stateSelect => {
    const form = stateSelect.closest('form');
    if (!form) return;

    // We now look for a text input for city (CF7 output)
    const cityInput = form.querySelector('input[name="city"]');
    if (!cityInput) return;

    // Remove CF7 autoComplete attribute if present to avoid native overlap
    cityInput.setAttribute('autocomplete', 'off');

    // Create custom autocomplete wrapper and list
    const wrapper = document.createElement('div');
    wrapper.className = 'autocomplete-wrapper';

    // Wrap the input
    cityInput.parentNode.insertBefore(wrapper, cityInput);
    wrapper.appendChild(cityInput);

    const autocompleteList = document.createElement('ul');
    autocompleteList.className = 'autocomplete-list';
    wrapper.appendChild(autocompleteList);

    let allCities = [];
    const citiesCache = {};

    // 1. Background Pre-fetch: Load all states' cities into memory silently when page loads
    Array.from(stateSelect.options).forEach(option => {
      const stateCode = option.value;
      if (stateCode && stateCode !== "Select State") {
        // Store the fetch promise in the cache
        citiesCache[stateCode] = fetch(`${CONFIG.CITIES_API_BASE}/${stateCode}/cities`)
          .then(res => {
            if (!res.ok) throw new Error('Network response was not ok');
            return res.text();
          })
          .then(textData => {
            try {
              const cities = JSON.parse(textData);
              if (Array.isArray(cities)) {
                return cities.map(city => typeof city === 'string' ? city : (city.name || city.city || city.value || JSON.stringify(city)));
              }
            } catch (e) {
              console.error("Error parsing pre-fetched cities JSON for", stateCode);
            }
            return [];
          })
          .catch(error => {
            console.error('Error prefetching cities for', stateCode, error);
            return [];
          });
      }
    });

    // Filter cities function
    const filterCities = (query) => {
      autocompleteList.innerHTML = '';
      if (!query) {
        autocompleteList.classList.remove('is-active');
        return;
      }

      const filtered = allCities.filter(city => city.toLowerCase().includes(query.toLowerCase()));

      if (filtered.length === 0) {
        autocompleteList.classList.remove('is-active');
        return;
      }

      filtered.forEach(cityName => {
        const li = document.createElement('li');
        // Highlight matching part
        const regex = new RegExp(`(${query})`, 'gi');
        li.innerHTML = cityName.replace(regex, '<strong>$1</strong>');

        li.addEventListener('mousedown', (e) => {
          // Use mousedown instead of click to fire before input blur
          e.preventDefault();
          cityInput.value = cityName;

          // Trigger change event for validation, but temporarily suppress filtering
          const originalFilterCities = filterCities;
          cityInput.removeEventListener('input', inputListener);
          const event = new Event('input', { bubbles: true });
          cityInput.dispatchEvent(event);
          cityInput.addEventListener('input', inputListener);

          autocompleteList.classList.remove('is-active');
        });

        autocompleteList.appendChild(li);
      });

      autocompleteList.classList.add('is-active');
    };

    // Input events
    const inputListener = (e) => filterCities(e.target.value);
    cityInput.addEventListener('input', inputListener);
    cityInput.addEventListener('focus', (e) => {
      if (allCities.length > 0) {
        // Show all list items early if no query, or filtered if there is
        filterCities(e.target.value || ' ');
      }
    });

    // Close autocomplete when clicking outside
    document.addEventListener('click', function (e) {
      if (e.target !== cityInput && !autocompleteList.contains(e.target)) {
        autocompleteList.classList.remove('is-active');
      }
    });

    stateSelect.addEventListener('change', async (e) => {
      const stateCode = e.target.value;

      // Update placeholder styling
      if (!stateCode || stateCode === "Select State" || stateCode === "Seleccione un estado" || stateCode === "---") {
        stateSelect.classList.add('is-placeholder');
      } else {
        stateSelect.classList.remove('is-placeholder');
      }

      // Clear current cities from text input and list
      cityInput.value = '';
      allCities = [];
      autocompleteList.classList.remove('is-active');
      cityInput.disabled = true;
      cityInput.placeholder = "Loading cities...";

      if (!stateCode || !citiesCache[stateCode]) {
        cityInput.placeholder = "Select a state first";
        return;
      }

      try {
        // 2. Await the cached promise (resolves instantly if already pre-fetched)
        allCities = await citiesCache[stateCode];

        if (allCities.length > 0) {
          cityInput.disabled = false;
          cityInput.placeholder = "Start typing a city...";
        } else {
          cityInput.placeholder = "No cities found";
        }
      } catch (error) {
        console.error('Error loading cities from cache:', error);
        cityInput.placeholder = "Error loading cities";
      }
    });

    // Provide initial placeholder text and styling
    if (!stateSelect.value || stateSelect.value === "Select State" || stateSelect.value === "Seleccione un estado" || stateSelect.value === "---") {
      cityInput.disabled = true;
      cityInput.placeholder = "Select a state first";
      stateSelect.classList.add('is-placeholder');
    }
  });

  // Handle Form Submission Logic
  const submitButtons = document.querySelectorAll(".franchise-submit-button");
  const formsSet = new Set();
  const overlay = document.getElementById("franchise-overlay-spinner");
  const overlayMsg = document.getElementById("franchise-overlay-message");
  const spinner = document.getElementById("franchise-spinner");

  // Carga única de reCAPTCHA Enterprise
  const SITE_KEY = CONFIG.RECAPTCHA_SITE_KEY;
  let recaptchaPromise = null;
  function loadRecaptchaOnce() {
    if (window.grecaptcha && window.grecaptcha.enterprise) return Promise.resolve();
    if (recaptchaPromise) return recaptchaPromise;
    recaptchaPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = `${CONFIG.RECAPTCHA_SCRIPT_URL}?render=${SITE_KEY}`;
      s.async = true; s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Failed to load reCAPTCHA Enterprise'));
      document.head.appendChild(s);
    });
    return recaptchaPromise;
  }

  async function getRecaptchaToken(action) {
    await loadRecaptchaOnce();
    await new Promise(res => window.grecaptcha.enterprise.ready(res));
    return window.grecaptcha.enterprise.execute(SITE_KEY, { action });
  }

  submitButtons.forEach((btn) => {
    const form = btn.closest("form");
    if (form && !formsSet.has(form)) {
      formsSet.add(form);
      let apiValidated = false; // Flag: API ya validó el reCAPTCHA

      form.addEventListener("submit", async (e) => {
        // Si la API ya validó, dejar que CF7 envíe el correo normalmente
        if (apiValidated) {
          apiValidated = false;
          return; // CF7 procesa el submit y envía el correo
        }

        e.preventDefault();
        e.stopImmediatePropagation(); // Bloquear CF7 hasta que la API valide

        const responseOutput = form.querySelector(".wpcf7-response-output");
        if (responseOutput) responseOutput.style.display = "none";

        if (overlay) overlay.classList.add("active");
        if (overlayMsg)
          overlayMsg.textContent =
            overlayMsg.dataset.sending || "Sending request...";
        if (spinner) spinner.style.display = "block";

        const formData = new FormData(form);

        const stateCode = formData.get("state") || "";
        const city = formData.get("city") || "";

        const data = {
          name: formData.get("full-name") || "",
          email: formData.get("email") || "",
          phone: (formData.get("phone") || "").replace(/\D/g, ""),
          state: stateCode,
          possible_address: city,
          notes: formData.get("notes") || "",
          source: CONFIG.FORM_SOURCE,
          languageId: CONFIG.LANGUAGE_IDS[pageLanguage()]
        };

        try {
          const dynamicAction = `FRANCHISE_FORM_${Date.now()}_${Math.random().toString(36).substring(2)}`;
          const token = await getRecaptchaToken(dynamicAction);
          data.captchaToken = token;

          const response = await fetch(
            CONFIG.FRANCHISE_API_URL,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(data),
            }
          );

          if (spinner) spinner.style.display = "none";

          if (response.ok) {
            // API validó OK → generar segundo token para validación server-side de CF7
            const cf7Token = await getRecaptchaToken('CF7_SUBMIT');
            let hiddenInput = form.querySelector('input[name="_recaptcha_cf7_token"]');
            if (!hiddenInput) {
              hiddenInput = document.createElement('input');
              hiddenInput.type = 'hidden';
              hiddenInput.name = '_recaptcha_cf7_token';
              form.appendChild(hiddenInput);
            }
            hiddenInput.value = cf7Token;

            apiValidated = true;
            form.requestSubmit(); // Re-dispara submit → CF7 lo procesa con el token
            if (overlayMsg)
              overlayMsg.textContent =
                overlayMsg.dataset.success ||
                "Your request was sent successfully!";
          } else {
            const errorText = await response.text();
            console.error("API 403 / Error Response:", errorText);
            if (overlayMsg)
              overlayMsg.textContent =
                (overlayMsg.dataset.error ||
                  "There was an error sending your request.") +
                " " +
                errorText;
          }
        } catch (error) {
          console.error('Submission Error:', error);
          if (spinner) spinner.style.display = "none";
          if (overlayMsg)
            overlayMsg.textContent =
              (overlayMsg.dataset.network || "Error sending request:") +
              " " +
              error.message;
        } finally {
          setTimeout(() => {
            if (overlay) overlay.classList.remove("active");
            if (spinner) spinner.style.display = "block";
            if (overlayMsg)
              overlayMsg.textContent =
                overlayMsg.dataset.sending || "Sending request...";
          }, 1200);
        }
      });
    }
  });
});
